import { useContext, useState, useEffect } from "react";
import { LocaleContext } from "../../contexts/LocaleContext";
import Pagination from "../../components/Pagination";
import { userActivityService } from "../../services/userActivityService";
import { cityService } from "../../services/cityService";
import Loading from "../../components/common/Loading";
import { params } from "../../config/params";
import { searchParams } from "../../config/searchParams";

const emptyFilters = {
  isActive: "",
  mobileFilter: "",
  cityId: "",
  role: "",
};

const UsersActivity = () => {
  const { t, lang } = useContext(LocaleContext);
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState([]);
  const [search, setSearch] = useState("");
  const [cities, setCities] = useState([]);
  const [filters, setFilters] = useState(emptyFilters);
  const [showFilters, setShowFilters] = useState(false);
  const [date, setDate] = useState(new Date().toISODateString());

  useEffect(() => {
    setLoading(true);
    getAllAsync();
    loadCities();
  }, []);

  const loadCities = async () => {
    try {
      const result = await cityService.getDropdownAsync();
      setCities(result || []);
    } catch (err) {
      console.error(err);
    }
  };

  const buildParams = (extraFilters = filters, selectedDate = date) => {
    const merged = { ...params, date: selectedDate };
    if (extraFilters.isActive !== "") merged.isActive = extraFilters.isActive === "true";
    if (extraFilters.mobileFilter) merged.mobileFilter = extraFilters.mobileFilter;
    if (extraFilters.cityId) merged.cityId = extraFilters.cityId;
    if (extraFilters.role) merged.role = extraFilters.role;
    return merged;
  };

  const getAllAsync = async (extraFilters = filters, selectedDate = date) => {
    try {
      const result = await userActivityService.getAllAsync(buildParams(extraFilters, selectedDate));
      setData(result);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const getAllSearchAsync = async (signal) => {
    try {
      const result = await userActivityService.getAllSearchAsync(
        { ...searchParams, ...buildParams() },
        signal
      );
      setData(result);
    } catch (err) {
      console.log(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (search.trim().length < 2) {
      params.pageNumber = 1;
      getAllAsync();
      return;
    }
    const controller = new AbortController();
    const delay = setTimeout(() => {
      searchParams.search = search.trim();
      searchParams.pageNumber = 1;
      getAllSearchAsync(controller.signal);
    }, 400);
    return () => {
      controller.abort();
      clearTimeout(delay);
    };
  }, [search]);

  const onDateChange = (e) => {
    const selected = e.target.value;
    setDate(selected);
    params.pageNumber = 1;
    setLoading(true);
    getAllAsync(filters, selected);
  };

  const onPageChange = (pageNumber) => {
    setLoading(true);
    if (!search) {
      params.pageNumber = pageNumber;
      getAllAsync();
      return;
    }
    searchParams.pageNumber = pageNumber;
    getAllSearchAsync();
  };

  const applyFilters = () => {
    params.pageNumber = 1;
    setLoading(true);
    setShowFilters(false);
    getAllAsync(filters);
  };

  const clearFilters = () => {
    setFilters(emptyFilters);
    params.pageNumber = 1;
    setLoading(true);
    getAllAsync(emptyFilters);
  };

  const handleFilterChange = (key, value) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
  };

  return (
    <>
      {loading && <Loading />}
      <section className="users-activity">
        <div className="default-heading">{t("usersActivity")}</div>
        <section className="section-grid">
          <div className="grid-card">
            <div className="grid-card-header">
              <strong>{t("usersActivity")} ({t("log")})</strong>
              <p>{t("activeUsersToday")}</p>
            </div>
            <div className="grid-card-body">
              <div className="table-header">
                <div className="table-header-icons hidden">
                  <div className="icon-box"><i className="fas fa-file-pdf"></i></div>
                  <div className="icon-box"><i className="fas fa-file-excel"></i></div>
                  <div className="icon-box"><i className="fas fa-print"></i></div>
                </div>
                <div className="table-search">
                  <div className="input-icon">
                    <input
                      type="date"
                      className="form-control"
                      value={date}
                      onChange={onDateChange}
                    />
                  </div>
                  <div className="input-icon input-icon-width">
                    <input
                      type="text"
                      value={search}
                      onChange={(e) => setSearch(e.target.value)}
                      className="form-control input-search"
                      placeholder={t("search")}
                    />
                    <i className="fas fa-search"></i>
                  </div>
                </div>
              </div>
              <div className="table-delete-option mt-3">
                <b>{t("allCustomers")}</b>
                <div className="d-flex align-items-center gap-2">
                  <i
                    className="fas fa-filter cursor-pointer"
                    onClick={() => setShowFilters((v) => !v)}
                  />
                  <span></span>
                </div>
              </div>
              {showFilters && (
                <div className="p-3 border rounded mb-3 bg-white">
                  <div className="row g-2">
                    <div className="col-12 col-md-3">
                      <label className="form-label fw-bold">{t("status")}</label>
                      <select
                        className="form-control form-control-sm"
                        value={filters.isActive}
                        onChange={(e) => handleFilterChange("isActive", e.target.value)}
                      >
                        <option value="">{t("all")}</option>
                        <option value="true">{t("active")}</option>
                        <option value="false">{t("inactive")}</option>
                      </select>
                    </div>
                    <div className="col-12 col-md-3">
                      <label className="form-label fw-bold">{t("mobileNo")}</label>
                      <input
                        type="text"
                        className="form-control form-control-sm"
                        value={filters.mobileFilter}
                        onChange={(e) => handleFilterChange("mobileFilter", e.target.value)}
                        placeholder={t("mobileNo")}
                      />
                    </div>
                    <div className="col-12 col-md-3">
                      <label className="form-label fw-bold">{t("city")}</label>
                      <select
                        className="form-control form-control-sm"
                        value={filters.cityId}
                        onChange={(e) => handleFilterChange("cityId", e.target.value)}
                      >
                        <option value="">{t("all")}</option>
                        {cities.map((city) => (
                          <option key={city.id} value={city.id}>
                            {city.name?.[lang]}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div className="col-12 col-md-3">
                      <label className="form-label fw-bold">{t("role")}</label>
                      <input
                        type="text"
                        className="form-control form-control-sm"
                        value={filters.role}
                        onChange={(e) => handleFilterChange("role", e.target.value)}
                        placeholder={t("role")}
                      />
                    </div>
                    <div className="col-12 d-flex gap-2 justify-content-end">
                      <button className="btn-default" onClick={applyFilters}>
                        {t("search")}
                      </button>
                      <button className="btn-secondary" onClick={clearFilters}>
                        {t("cancel")}
                      </button>
                    </div>
                  </div>
                </div>
              )}
              <div className="my-table">
                <table className="table">
                  <thead>
                    <tr>
                      <th><input type="checkbox" /></th>
                      <th>{t("userName")}</th>
                      <th>{t("mobileNo")}</th>
                      <th>{t("role")}</th>
                      <th>{t("city")}</th>
                      <th>{t("status")}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data?.items?.map((item) => (
                      <tr key={item.id}>
                        <td><input type="checkbox" /></td>
                        <td>{item.fullName}</td>
                        <td>{item.mobileNumber}</td>
                        <td>{item.role}</td>
                        <td>{item.city?.[lang]}</td>
                        <td>
                          <span className={`badge ${item.isActive ? "bg-success" : "bg-secondary"}`}>
                            {item.isActive ? t("active") : t("inactive")}
                          </span>
                        </td>
                      </tr>
                    ))}
                    {data?.items?.length === 0 && (
                      <tr>
                        <td colSpan="100%" className="text-center py-3">
                          {t("noResultsFound")}
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
            <div className="grid-footer">
              {data && (
                <Pagination
                  t={t}
                  pageNumber={data.pageNumber}
                  totalPages={data.totalPages}
                  hasNextPage={data.hasNextPage}
                  hasPreviousPage={data.hasPreviousPage}
                  onPageChange={onPageChange}
                />
              )}
            </div>
          </div>
        </section>
      </section>
    </>
  );
};

export default UsersActivity;
